import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";
import { ProductCard } from "@/components/ProductCard";
import { Seo, faqLd } from "@/components/Seo";
import { AnimatedCounter, Reveal } from "@/components/Motion";
import { HEATHER_IMG, HEATHER_HERO_IMG, HEATHER_NAME, HEATHER_TITLE } from "@/lib/brand";
import { useAuth } from "@/context/AuthContext";
import WisconsinMap from "@/components/home/WisconsinMap";
import JustLockedTicker from "@/components/home/JustLockedTicker";
import SchoolUrgencyStrip from "@/components/home/SchoolUrgencyStrip";
import VisitBoutique from "@/components/home/VisitBoutique";
import {
  ArrowUpRight, Sparkles, School as SchoolIcon, ShieldCheck, Camera,
  HeartHandshake, PartyPopper, Trophy, Crown,
  Lock, Truck, Users, Timer, CheckCircle2,
} from "lucide-react";

const FAQS = [
  { q: "Where can I buy prom dresses in Wisconsin?", a: "Heather's Boutique is Wisconsin's premier online formalwear destination for prom, graduation and homecoming. We ship statewide and offer school-exclusive collections, AI styling, and virtual try-on so you can shop luxury dresses and tuxedos from anywhere in Wisconsin." },
  { q: "How does the dress lock reservation work?", a: "Every dress can be locked by only one student per school at a time. When you lock a dress, it's held exclusively for you for 2 hours so no one else at your school can buy the same look. Purchasing permanently secures it as yours." },
  { q: "Can I try on prom dresses virtually?", a: "Yes. Upload a selfie and our AI virtual try-on shows you styled in any dress or tuxedo. VIP members get unlimited try-ons every season." },
  { q: "Do you carry tuxedos and accessories?", a: "Yes — Heather's Boutique offers tailored tuxedos, dinner jackets, heels, and clutches alongside our dress collections so your whole squad can coordinate." },
  { q: "How do I unlock my school's exclusive collection?", a: "Register with your school email address. We verify your school by email domain, unlocking dress locks, squad planning, your school portal, and your school's memory hub." },
];

const HERO = "https://images.unsplash.com/photo-1595777457583-95e059d581b8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxwcm9tJTIwZHJlc3MlMjBlZGl0b3JpYWwlMjBmYXNoaW9ufGVufDB8fHx8MTc4Mjg3MzIyNnww&ixlib=rb-4.1.0&q=85";
const HERO2 = "https://images.unsplash.com/photo-1623580674393-edf6eb7090f8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHw0fHxwcm9tJTIwZHJlc3MlMjBlZGl0b3JpYWwlMjBmYXNoaW9ufGVufDB8fHx8MTc4Mjg3MzIyNnww&ixlib=rb-4.1.0&q=85";
const BOUTIQUE = "https://images.unsplash.com/photo-1581775120934-9851ea6cf3e4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBib3V0aXF1ZSUyMGludGVyaW9yJTIwY2hhbXBhZ25lfGVufDB8fHx8MTc4Mjg3MzIyNnww&ixlib=rb-4.1.0&q=85";
// AI Try-On showcase (Heather's real before/after)
const TRYON_BEFORE = "https://customer-assets.emergentagent.com/job_site-enhancer-58/artifacts/19pn37uw_Heather%27s%20Boutique%20Before%20Now.webp";
const TRYON_AFTER = "https://customer-assets.emergentagent.com/job_site-enhancer-58/artifacts/s8t1t9nh_Heather%27s%20Boutique%20After.webp";

const OCCASIONS = [
  { label: "Prom", to: "/prom-dresses", img: "https://customer-assets-39nsmqrw.emergentagent.net/job_site-enhancer-58/artifacts/73m51svm_Prom%20Dresses%20in%20Wisconsin.png", tag: "The Main Event" },
  { label: "Graduation", to: "/graduation-dresses", img: "https://customer-assets-39nsmqrw.emergentagent.net/job_site-enhancer-58/artifacts/awgue1e8_Graduation%20Dresses%20In%20Wisconsin%2C%20Graduation%20Dress%20Shops%20in%20WI.png", tag: "Turn The Tassel" },
  { label: "Homecoming", to: "/homecoming-dresses", img: "https://customer-assets-39nsmqrw.emergentagent.net/job_site-enhancer-58/artifacts/bhznmw71_Homecoming%20Dress%20Stores%20In%20Wisconsin.%20Homecoming%20Dresses%20in%20WI.png", tag: "Fall Romance" },
];

export default function Home() {
  const { user } = useAuth();
  const [featured, setFeatured] = useState([]);
  const [memories, setMemories] = useState([]);
  const [stats, setStats] = useState(null);
  const [tuxedos, setTuxedos] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [lookbook, setLookbook] = useState([]);
  const [trending, setTrending] = useState({ school: null, products: [], hot_schools: [] });

  useEffect(() => {
    api.products({ sort: "trending" }).then(({ data }) => setFeatured(data.filter((p) => p.featured).slice(0, 4)));
    api.memories({}).then(({ data }) => setMemories(data.slice(0, 3)));
    api.homepageStats().then(({ data }) => setStats(data)).catch(() => {});
    api.products({ category: "tuxedos", sort: "trending" }).then(({ data }) => setTuxedos((data || []).slice(0, 4))).catch(() => {});
    api.promCupLeaderboard(8).then(({ data }) => setLeaderboard(data || [])).catch(() => {});
    api.lookbookCommunity().then(({ data }) => setLookbook((data || []).slice(0, 6))).catch(() => {});
    api.trendingBySchool(user?.school_id).then(({ data }) => setTrending(data || { hot_schools: [] })).catch(() => {});
  }, [user?.school_id]);

  const fmt = (n) => (n == null ? "—" : n.toLocaleString());
  const fmtMoney = (n) => {
    if (n == null) return "—";
    if (n >= 1000) return `$${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}K`;
    return `$${Math.round(n)}`;
  };

  return (
    <div data-testid="home-page">
      <Seo
        title="WI Prom, Graduation & Homecoming Dresses"
        description="Luxury prom, grad & homecoming dresses + tuxedos. Lock your look for your school, get AI fit guidance, try on with a selfie."
        path="/"
        jsonLd={[
          faqLd(FAQS),
          { "@context": "https://schema.org", "@type": "WebSite", name: "Heather's Boutique", url: "https://wi.heathersboutique.com/", potentialAction: { "@type": "SearchAction", target: "https://wi.heathersboutique.com/shop?q={search_term_string}", "query-input": "required name=search_term_string" } },
        ]}
      />
      {/* HERO — split: text left, live WI map right */}
      <section className="relative max-w-[1400px] mx-auto px-5 sm:px-8 pt-10 md:pt-16">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <p className="font-label text-[10px] text-rosemetal mb-5 tracking-[0.25em] animate-fade-up">◉ PROM · GRADUATION · HOMECOMING</p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-tight animate-fade-up text-balance" style={{ animationDelay: "80ms" }}>
              The dress they'll<br /> talk about<br /> <span className="italic text-rosemetal">all summer.</span>
            </h1>
            <p className="font-body text-base text-muted-foreground mt-6 max-w-md leading-relaxed animate-fade-up" style={{ animationDelay: "160ms" }}>
              A luxury formalwear house built for your school. Lock your look before anyone else can, style it with Heather, and never show up in the same dress twice.
            </p>
            <div className="flex flex-wrap gap-3 mt-8 animate-fade-up" style={{ animationDelay: "240ms" }}>
              <Link to="/prom-dresses" data-testid="hero-shop-button" className="bg-ink text-cream px-7 py-3.5 font-label text-[10px] hover:bg-rosemetal transition-colors rounded-full tracking-widest">SHOP THE PROM EDIT</Link>
              <Link to="/try-on" data-testid="hero-tryon-button" className="border border-ink px-7 py-3.5 font-label text-[10px] hover:bg-ink hover:text-cream transition-colors rounded-full tracking-widest inline-flex items-center gap-2"><Sparkles className="w-3 h-3" /> TRY ON WITH AI</Link>
            </div>
            {/* Trust rail */}
            <div className="mt-10 flex flex-wrap items-center gap-6 font-label text-[9px] text-muted-foreground tracking-[0.22em] animate-fade-up" style={{ animationDelay: "320ms" }}>
              <span>🏆 {stats?.schools_count ?? "258"} WI SCHOOLS</span>
              <span>⚡ 15-SEC AI TRY-ON</span>
              <span>💎 EDITORIAL BRANDS</span>
            </div>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2 relative animate-fade-up" style={{ animationDelay: "120ms" }}>
            <div className="relative">
              {/* soft glow behind the map */}
              <div className="absolute inset-0 bg-gradient-to-br from-blush/50 via-cream to-champagne/40 blur-3xl -z-10 rounded-full scale-90 opacity-70" aria-hidden />
              <WisconsinMap
                schoolsAddedToday={stats?.joined_today ?? 0}
                fundsRaisedWeek={stats?.funds_raised_week ?? 0}
              />
            </div>
          </div>
        </div>
      </section>

      {/* SCHOOL URGENCY — weekly locks strip */}
      <SchoolUrgencyStrip />

      {/* JUST LOCKED — live ticker */}
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <JustLockedTicker />
      </div>

      {/* STATS STRIP — live from DB with animated count-up */}
      <section data-testid="stats-strip" className="max-w-[1400px] mx-auto px-5 sm:px-8 mt-16">        <Reveal>
        <div className="border-t border-b border-border py-10 grid grid-cols-3 md:grid-cols-6 gap-4 md:gap-6">
          {[
            { v: stats?.schools_count ?? 0, l: "Schools Connected" },
            { v: stats?.joined_this_month ?? 0, l: "Joined This Month" },
            { v: stats?.dresses_tried_on ?? 0, l: "Dresses Tried On" },
            { v: stats?.orders_placed ?? 0, l: "Orders Placed" },
            { v: stats?.funds_raised ?? 0, l: "Funds Raised", money: true },
            { v: stats?.students_served ?? 0, l: "Students Served" },
          ].map((s, i) => (
            <motion.div
              key={i}
              className="text-center"
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="font-display text-3xl md:text-5xl leading-none">
                {s.money ? (
                  <AnimatedCounter value={s.v} prefix="$" format={(n) => (n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}K` : Math.round(n).toString())} />
                ) : (
                  <AnimatedCounter value={s.v} />
                )}
              </div>
              <div className="font-label text-[9px] text-muted-foreground mt-2 tracking-widest uppercase">{s.l}</div>
            </motion.div>
          ))}
        </div>
        </Reveal>
      </section>

      {/* AI TRY-ON SHOWCASE — dark cinematic section */}
      <section data-testid="ai-tryon-showcase" className="mt-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-ink via-[#1a0e0f] to-[#2a0f14]" />
        <div className="absolute inset-0 opacity-25 mix-blend-screen" style={{ background: "radial-gradient(1200px 600px at 20% 30%, rgba(215,138,132,0.7), transparent), radial-gradient(900px 500px at 80% 70%, rgba(230,196,143,0.5), transparent)" }} />
        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 py-24 md:py-32 grid md:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="text-ivory">
              <span className="inline-flex items-center gap-2 border border-champagne/40 rounded-full px-3 py-1 font-label text-[10px] text-champagne">
                <Sparkles className="w-3 h-3" /> POWERED BY OPENAI · GPT IMAGE 1
              </span>
              <h2 className="font-display text-5xl md:text-7xl mt-6 leading-[0.95]">
                See it on you<br />
                <span className="italic text-champagne">before</span> you ever<br />
                step into the boutique.
              </h2>
              <p className="font-body text-ivory/70 mt-6 max-w-lg leading-relaxed">
                Upload one selfie. Our AI dresses you in any of our 2,500+ looks in seconds — same face, same body, real dress. Magazine quality, every time.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/try-on" data-testid="hero-tryon-cta" className="inline-flex items-center gap-2 bg-champagne text-ink px-7 py-4 font-label text-[10px] rounded-full hover:bg-ivory transition-colors">
                  <Sparkles className="w-4 h-4" /> START YOUR TRY-ON
                </Link>
                <Link to="/lookbook" className="inline-flex items-center gap-2 border border-ivory/40 text-ivory px-7 py-4 font-label text-[10px] rounded-full hover:bg-ivory/10 transition-colors">
                  SEE THE LOOKBOOK →
                </Link>
              </div>
            </div>
          </Reveal>
          <div className="relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, rotate: -3 }}
              whileInView={{ opacity: 1, scale: 1, rotate: -2 }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="aspect-[3/4] max-w-md ml-auto rounded-sm overflow-hidden shadow-2xl relative group"
              data-testid="tryon-after-large"
            >
              <img src={TRYON_AFTER} alt="AI Try-On result — gold sequin gown" className="w-full h-full object-cover object-top" />
              <div className="absolute top-3 left-3 bg-rosemetal text-ivory px-2.5 py-1 font-label text-[9px] tracking-widest rounded-sm z-20">AFTER</div>

              {/* Scan reveal — sweeps top→bottom on view, then loops subtly */}
              <motion.div
                aria-hidden
                className="absolute inset-x-0 h-24 pointer-events-none z-10 mix-blend-screen"
                style={{
                  background: "linear-gradient(180deg, transparent 0%, rgba(230,196,143,0.35) 40%, rgba(255,255,255,0.85) 50%, rgba(230,196,143,0.35) 60%, transparent 100%)",
                  filter: "blur(2px)",
                }}
                initial={{ y: "-30%", opacity: 0 }}
                whileInView={{
                  y: ["-30%", "130%", "-30%"],
                  opacity: [0, 1, 1, 0],
                }}
                viewport={{ once: false, margin: "-120px" }}
                transition={{
                  duration: 5.5,
                  times: [0, 0.4, 0.6, 1],
                  ease: "easeInOut",
                  repeat: Infinity,
                  repeatDelay: 4,
                }}
              />
              {/* Reveal curtain — masks bottom on first view, wipes up */}
              <motion.div
                aria-hidden
                className="absolute inset-0 pointer-events-none z-10 bg-gradient-to-t from-ink via-ink/70 to-transparent"
                initial={{ opacity: 0.9, y: 0 }}
                whileInView={{ opacity: 0, y: "-100%" }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}
              />
              {/* "AI processing…" pill flashes during scan */}
              <motion.div
                className="absolute bottom-3 right-3 z-20 inline-flex items-center gap-1.5 bg-ink/70 backdrop-blur text-champagne px-2.5 py-1 rounded-full font-label text-[8px] tracking-widest"
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: [0, 1, 1, 0], y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 2.2, delay: 0.4, times: [0, 0.2, 0.8, 1] }}
              >
                <Sparkles className="w-2.5 h-2.5" />
                AI STYLING
              </motion.div>
              {/* Sparkles */}
              {[{ t: "18%", l: "22%", d: 0.1 }, { t: "38%", l: "78%", d: 0.6 }, { t: "62%", l: "18%", d: 1.1 }, { t: "80%", l: "68%", d: 1.6 }].map((s, i) => (
                <motion.span
                  key={i}
                  className="absolute z-10 pointer-events-none"
                  style={{ top: s.t, left: s.l }}
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: [0, 1, 0], scale: [0, 1.2, 0] }}
                  viewport={{ once: false, margin: "-120px" }}
                  transition={{ duration: 1.8, delay: s.d, repeat: Infinity, repeatDelay: 6 }}
                >
                  <Sparkles className="w-3.5 h-3.5 text-champagne drop-shadow-[0_0_6px_rgba(230,196,143,0.9)]" />
                </motion.span>
              ))}
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9, rotate: 6 }}
              whileInView={{ opacity: 1, scale: 1, rotate: 4 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="aspect-[3/4] max-w-[220px] absolute -bottom-6 -left-2 md:left-6 rounded-sm overflow-hidden shadow-2xl border-4 border-ivory z-30"
              data-testid="tryon-before-small"
            >
              <img src={TRYON_BEFORE} alt="AI Try-On original selfie" className="w-full h-full object-cover object-top" />
              <div className="absolute top-2 left-2 bg-ink/80 backdrop-blur text-ivory px-2 py-0.5 font-label text-[8px] tracking-widest rounded-sm">BEFORE</div>
            </motion.div>
            {/* Connecting arrow between BEFORE and AFTER */}
            <motion.svg
              aria-hidden
              className="absolute z-30 pointer-events-none hidden md:block"
              style={{ bottom: "18%", left: "38%" }}
              width="90"
              height="30"
              viewBox="0 0 90 30"
              initial={{ opacity: 0, x: -12 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 1.3 }}
            >
              <motion.path
                d="M 4 15 C 30 4, 55 26, 82 12"
                fill="none"
                stroke="#e6c48f"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeDasharray="4 4"
                initial={{ pathLength: 0 }}
                whileInView={{ pathLength: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: 1.3, ease: "easeOut" }}
              />
              <motion.path
                d="M 78 8 L 84 12 L 78 18"
                fill="none"
                stroke="#e6c48f"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: 2.2 }}
              />
            </motion.svg>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — editorial zigzag playbook */}
      <section data-testid="how-it-works" className="relative mt-32 py-24 md:py-32 overflow-hidden">
        {/* Layered background */}
        <div className="absolute inset-0 bg-gradient-to-b from-ivory via-cream/50 to-ivory" aria-hidden />
        <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-rosemetal/40 to-transparent" aria-hidden />
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-blush/25 blur-3xl -z-0 opacity-60" aria-hidden />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] rounded-full bg-champagne/20 blur-3xl -z-0 opacity-60" aria-hidden />

        <div className="relative max-w-[1200px] mx-auto px-5 sm:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="text-center mb-20 md:mb-28"
          >
            <p className="font-label text-[10px] text-rosemetal tracking-[0.35em] mb-5 inline-flex items-center gap-2">
              <span className="w-6 h-px bg-rosemetal/50" /> THE HEATHER'S PLAYBOOK <span className="w-6 h-px bg-rosemetal/50" />
            </p>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] max-w-4xl mx-auto text-ink">
              From <span className="italic text-rosemetal">"nothing to wear"</span><br />
              to <em className="not-italic">unforgettable.</em>
            </h2>
            <p className="font-body text-base md:text-lg text-ink/60 mt-8 max-w-2xl mx-auto leading-relaxed">
              Four steps. One dress no one else at your school will wear. All wrapped in a
              stylist experience built for Wisconsin students &amp; the parents who love them.
            </p>
          </motion.div>

          {/* Zigzag steps */}
          <div className="space-y-24 lg:space-y-32">
            {[
              {
                n: "01",
                eyebrow: "STEP ONE · TWO MINUTES",
                Icon: ShieldCheck,
                title: "Verify your school.",
                body: "Sign in with your school email — or as a parent — and unlock your school's exclusive boutique. We check the domain so only students at your school can lock dresses on your school's list.",
                pill: { Icon: SchoolIcon, text: `${stats?.schools_count ?? 258} WI SCHOOLS VERIFIED` },
                cta: { to: "/schools", label: "FIND YOUR SCHOOL" },
                visual: (
                  <div className="relative aspect-[4/5] w-full max-w-[440px] mx-auto rounded-sm overflow-hidden bg-gradient-to-br from-cream via-ivory to-blush/60 border border-cream shadow-[0_20px_60px_-15px_rgba(183,110,121,0.25)]">
                    <div className="absolute inset-0 p-8 flex flex-col justify-between">
                      <div>
                        <p className="font-label text-[9px] text-rosemetal tracking-[0.32em]">◉ WISCONSIN PROM NETWORK</p>
                        <p className="font-label text-[9px] text-ink/40 tracking-widest mt-1">STUDENT ID · #WI-2027</p>
                      </div>
                      <div>
                        <p className="font-label text-[10px] text-ink/50 tracking-widest">YOUR SCHOOL</p>
                        <h4 className="font-display text-3xl md:text-4xl leading-[1.05] text-ink mt-1">Oconomowoc<br />High School</h4>
                        <div className="mt-4 inline-flex items-center gap-2 bg-ink text-cream rounded-full px-3.5 py-1.5">
                          <CheckCircle2 className="w-3.5 h-3.5 text-champagne" strokeWidth={2} />
                          <span className="font-label text-[9px] tracking-[0.28em]">VERIFIED</span>
                        </div>
                      </div>
                      <div className="flex items-end justify-between">
                        <div>
                          <p className="font-label text-[9px] text-ink/40 tracking-widest">PROM NIGHT</p>
                          <p className="font-display italic text-xl text-ink mt-0.5">Apr 24, 2027</p>
                        </div>
                        <div className="w-14 h-14 rounded-full bg-rosemetal/15 border border-rosemetal/40 flex items-center justify-center">
                          <ShieldCheck className="w-6 h-6 text-rosemetal" strokeWidth={1.5} />
                        </div>
                      </div>
                    </div>
                  </div>
                ),
              },
              {
                n: "02",
                eyebrow: "STEP TWO · ~15 SECONDS",
                Icon: Camera,
                title: "Try any dress on with AI.",
                body: "Upload one selfie. Our AI stylist dresses you in any gown or tux in the boutique in about 15 seconds — no shipping, no returns, no fitting room lighting lies. Then compare your top three side-by-side.",
                pill: { Icon: Timer, text: "SEE IT IN 15 SECONDS" },
                cta: { to: "/try-on", label: "TRY IT NOW" },
                visual: (
                  <div className="relative">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="relative aspect-[3/4] rounded-sm overflow-hidden bg-cream border border-cream">
                        <img src={TRYON_BEFORE} alt="Before" className="w-full h-full object-cover object-top" />
                        <div className="absolute top-3 left-3 bg-ink/85 backdrop-blur text-cream font-label text-[9px] px-2 py-1 rounded-sm tracking-widest">BEFORE</div>
                      </div>
                      <div className="relative aspect-[3/4] rounded-sm overflow-hidden bg-cream border border-champagne/40 shadow-[0_20px_60px_-15px_rgba(230,196,143,0.4)]">
                        <img src={TRYON_AFTER} alt="After AI Try-On" className="w-full h-full object-cover object-top" />
                        <div className="absolute top-3 left-3 bg-champagne text-ink font-label text-[9px] px-2 py-1 rounded-sm tracking-widest inline-flex items-center gap-1">
                          <Sparkles className="w-2.5 h-2.5" strokeWidth={2.5} /> AFTER
                        </div>
                        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-ink text-cream rounded-full px-3 py-1.5 font-label text-[9px] tracking-[0.28em] shadow-lg whitespace-nowrap inline-flex items-center gap-1.5">
                          <Sparkles className="w-3 h-3 text-champagne" strokeWidth={2} /> AI TRY-ON · 1 SELFIE
                        </div>
                      </div>
                    </div>
                  </div>
                ),
              },
              {
                n: "03",
                eyebrow: "STEP THREE · 2-HOUR HOLD",
                Icon: Lock,
                title: "Lock it — she's yours.",
                body: "Reserve your dress exclusively for your school. Once you lock it, no one else at your school can wear it. Two hour hold to think it over; buy it and she's yours forever — the mirror moment nobody duplicates.",
                pill: { Icon: Lock, text: "1 DRESS · 1 STUDENT · YOUR SCHOOL" },
                cta: { to: "/prom-dresses", label: "BROWSE PROM" },
                visual: (
                  <div className="relative max-w-[440px] mx-auto">
                    <div className="relative aspect-[3/4] rounded-sm overflow-hidden bg-blush border border-cream shadow-[0_20px_60px_-15px_rgba(10,10,10,0.35)]">
                      <img
                        src="https://customer-assets-39nsmqrw.emergentagent.net/job_site-enhancer-58/artifacts/73m51svm_Prom%20Dresses%20in%20Wisconsin.png"
                        alt="Locked dress"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent" />
                      {/* Corner ribbon */}
                      <div className="absolute top-4 right-4 bg-rosemetal text-ivory rounded-full px-3 py-1.5 font-label text-[9px] tracking-[0.28em] inline-flex items-center gap-1.5 shadow-md">
                        <Lock className="w-3 h-3" strokeWidth={2.2} /> LOCKED
                      </div>
                      {/* Bottom info */}
                      <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between text-ivory">
                        <div>
                          <p className="font-label text-[9px] text-champagne tracking-widest">TAKEN AT</p>
                          <p className="font-display text-lg leading-tight mt-0.5">Oconomowoc HS</p>
                        </div>
                        <div className="text-right bg-ink/60 backdrop-blur-md rounded-sm px-3 py-2">
                          <p className="font-label text-[8px] text-cream/70 tracking-widest">HELD FOR YOU</p>
                          <p className="font-display italic text-2xl leading-none mt-1 tabular-nums">1:47:32</p>
                        </div>
                      </div>
                    </div>
                    {/* Floating countdown badge */}
                    <div className="absolute -left-3 top-8 bg-ivory border border-rosemetal/40 rounded-sm px-3 py-2 shadow-lg rotate-[-6deg]">
                      <p className="font-label text-[8px] text-rosemetal tracking-widest">HEATHER'S BOUTIQUE</p>
                      <p className="font-display italic text-base leading-tight mt-0.5">She's off the market at your school.</p>
                    </div>
                  </div>
                ),
              },
              {
                n: "04",
                eyebrow: "STEP FOUR · THE NIGHT",
                Icon: PartyPopper,
                title: "Wear it. Share it. Get crowned.",
                body: "Pick her up from Heather in Oconomowoc — or ship free anywhere in Wisconsin. After the night, share your look to the community lookbook, rack up loves, and win Most-Loved of the Week at your school.",
                pill: { Icon: Truck, text: "FREE WI SHIPPING · 3-DAY DELIVERY" },
                cta: { to: "/lookbook", label: "SEE THE LOOKBOOK" },
                visual: (
                  <div className="grid grid-cols-2 gap-3 max-w-[460px] mx-auto">
                    <div className="row-span-2 rounded-sm overflow-hidden bg-blush border border-cream shadow-md">
                      <img src="https://customer-assets-39nsmqrw.emergentagent.net/job_site-enhancer-58/artifacts/awgue1e8_Graduation%20Dresses%20In%20Wisconsin%2C%20Graduation%20Dress%20Shops%20in%20WI.png" alt="Grad night look" className="w-full h-full object-cover object-top" />
                    </div>
                    <div className="aspect-[4/5] rounded-sm overflow-hidden bg-blush border border-cream shadow-md">
                      <img src="https://customer-assets-39nsmqrw.emergentagent.net/job_site-enhancer-58/artifacts/bhznmw71_Homecoming%20Dress%20Stores%20In%20Wisconsin.%20Homecoming%20Dresses%20in%20WI.png" alt="Homecoming look" className="w-full h-full object-cover object-top" />
                    </div>
                    <div className="aspect-[4/5] rounded-sm overflow-hidden bg-gradient-to-br from-ink to-[#1a0e0f] text-cream border border-champagne/30 shadow-lg flex flex-col items-center justify-center p-4 text-center">
                      <Crown className="w-8 h-8 text-champagne mb-3" strokeWidth={1.5} />
                      <p className="font-label text-[9px] text-champagne tracking-[0.28em]">MOST-LOVED</p>
                      <p className="font-display italic text-xl leading-tight mt-2">This week's<br />queen</p>
                      <div className="mt-3 pt-3 border-t border-cream/15 w-full">
                        <p className="font-label text-[8px] text-cream/50 tracking-widest">+47 LOVES</p>
                      </div>
                    </div>
                  </div>
                ),
              },
            ].map((step, i) => (
              <motion.article
                key={step.n}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`how-step-${step.n}`}
                className={`grid lg:grid-cols-2 gap-14 lg:gap-20 items-center ${i % 2 === 1 ? "lg:[&>div:first-child]:order-2" : ""}`}
              >
                {/* Visual */}
                <div className="relative">
                  {step.visual}
                </div>

                {/* Copy */}
                <div>
                  <div className="flex items-baseline gap-5 mb-6">
                    <span className="font-display italic text-7xl md:text-8xl text-rosemetal/70 leading-none tracking-tight">{step.n}</span>
                    <span className="h-px flex-1 bg-gradient-to-r from-rosemetal/50 via-rosemetal/20 to-transparent" />
                  </div>
                  <p className="font-label text-[10px] text-rosemetal tracking-[0.3em] mb-4">{step.eyebrow}</p>
                  <div className="flex items-start gap-4 mb-5">
                    <span className="w-12 h-12 shrink-0 rounded-full bg-ivory border border-rosemetal/40 flex items-center justify-center shadow-sm">
                      <step.Icon className="w-5 h-5 text-rosemetal" strokeWidth={1.5} />
                    </span>
                    <h3 className="font-display text-3xl md:text-4xl leading-[1.05] text-ink pt-1">{step.title}</h3>
                  </div>
                  <p className="font-body text-base md:text-lg text-ink/70 leading-relaxed max-w-md">{step.body}</p>

                  <div className="mt-7 flex flex-wrap items-center gap-4">
                    <div className="inline-flex items-center gap-2 bg-blush border border-rosemetal/25 rounded-full px-4 py-2">
                      <step.pill.Icon className="w-3.5 h-3.5 text-rosemetal" strokeWidth={2} />
                      <span className="font-label text-[9px] text-rosemetal tracking-[0.28em]">{step.pill.text}</span>
                    </div>
                    <Link
                      to={step.cta.to}
                      data-testid={`how-step-${step.n}-cta`}
                      className="inline-flex items-center gap-2 font-label text-[10px] tracking-[0.28em] text-ink hover:text-rosemetal transition-colors border-b border-ink/30 hover:border-rosemetal pb-1"
                    >
                      {step.cta.label} <ArrowUpRight className="w-3 h-3" />
                    </Link>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>

          {/* Supporting benefit rail */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mt-32 pt-16 border-t border-cream"
          >
            <p className="text-center font-label text-[10px] text-rosemetal tracking-[0.32em] mb-10">◉ PLUS, THE HEATHER'S DIFFERENCE</p>
            <div className="grid md:grid-cols-3 gap-10">
              {[
                { Icon: HeartHandshake, eyebrow: "IN-PERSON, IF YOU WANT", title: "Come see Heather.", body: "Book a private fitting in Oconomowoc. She's tailored 1,200+ students herself — every stitch, every silhouette, dialed to you.", href: "/contact" },
                { Icon: Users, eyebrow: "BUILT FOR PARENTS", title: "Mom pays, student picks.", body: "Linked parent accounts let a parent check out while their student styles. Try-on stays student-only — private and their own.", href: "/parents" },
                { Icon: Trophy, eyebrow: "FUNDS YOUR SCHOOL", title: "Every look gives back.", body: "A slice of every dress goes to your school's booster program. Watch your school climb the statewide Prom Cup leaderboard.", href: "/prom-cup" },
              ].map((b, i) => (
                <motion.div
                  key={b.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  className="group"
                  data-testid={`how-benefit-${i}`}
                >
                  <div className="w-11 h-11 rounded-full bg-ivory border border-rosemetal/30 flex items-center justify-center mb-5 group-hover:bg-rosemetal group-hover:border-rosemetal transition-colors">
                    <b.Icon className="w-5 h-5 text-rosemetal group-hover:text-ivory transition-colors" strokeWidth={1.5} />
                  </div>
                  <p className="font-label text-[9px] text-rosemetal tracking-[0.28em] mb-2">{b.eyebrow}</p>
                  <h4 className="font-display text-2xl md:text-3xl text-ink leading-tight mb-3">{b.title}</h4>
                  <p className="font-body text-sm text-ink/65 leading-relaxed mb-4">{b.body}</p>
                  <Link to={b.href} className="inline-flex items-center gap-1.5 font-label text-[9px] tracking-[0.28em] text-rosemetal hover:text-ink transition-colors">
                    LEARN MORE <ArrowUpRight className="w-3 h-3" />
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Final CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mt-20 text-center"
          >
            <p className="font-display italic text-2xl md:text-3xl text-ink/80 mb-6">Ready when you are.</p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/prom-dresses"
                data-testid="how-final-cta-shop"
                className="bg-ink text-cream px-8 py-4 font-label text-[10px] tracking-[0.3em] rounded-full hover:bg-rosemetal transition-colors"
              >
                START WITH PROM →
              </Link>
              <Link
                to="/try-on"
                data-testid="how-final-cta-tryon"
                className="border border-ink px-8 py-4 font-label text-[10px] tracking-[0.3em] rounded-full hover:bg-ink hover:text-cream transition-colors inline-flex items-center gap-2"
              >
                <Sparkles className="w-3 h-3" /> TRY ON WITH AI
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* OCCASIONS */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 mt-28">
        <div className="flex items-end justify-between mb-8">
          <h2 className="font-display text-4xl sm:text-5xl">Choose your occasion</h2>
          <Link to="/try-on" className="font-label text-[10px] text-rosemetal flex items-center gap-1">All looks <ArrowUpRight className="w-3 h-3" /></Link>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {OCCASIONS.map((o, i) => (
            <Link key={o.label} to={o.to} data-testid={`occasion-${o.label.toLowerCase()}`} className="group relative overflow-hidden animate-fade-up" style={{ animationDelay: `${i * 90}ms` }}>
              <div className="aspect-[3/4] overflow-hidden bg-blush">
                <img src={o.img} alt={o.label} className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 text-ivory">
                <div className="font-label text-[9px] text-champagne">{o.tag}</div>
                <div className="font-display text-4xl">{o.label}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* TRENDING NOW — either your school's top locks or statewide hot schools */}
      <section data-testid="trending-by-school" className="max-w-[1400px] mx-auto px-5 sm:px-8 mt-28">
        {trending.school && trending.products?.length > 0 ? (
          <>
            <div className="flex items-end justify-between mb-8">
              <div>
                <p className="font-label text-[10px] text-rosemetal tracking-widest">TRENDING AT {trending.school.name?.toUpperCase()}</p>
                <h2 className="font-display text-4xl sm:text-5xl mt-2 leading-[0.95]">
                  What your school<br /><span className="italic text-rosemetal">is locking now.</span>
                </h2>
              </div>
              <Link to={`/schools/${trending.school.slug || trending.school.id}`} className="hidden md:flex font-label text-[10px] text-rosemetal items-center gap-1">
                Full school portal <ArrowUpRight className="w-3 h-3" />
              </Link>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-10">
              {trending.products.slice(0, 4).map((p, i) => (
                <div key={p.id} className="relative">
                  <span className="absolute -top-2 -left-2 z-10 bg-rosemetal text-cream px-2.5 py-1 rounded-full font-label text-[9px] tracking-widest">
                    {p.lock_count} 🔒 THIS MONTH
                  </span>
                  <ProductCard product={p} index={i} />
                </div>
              ))}
            </div>
          </>
        ) : trending.hot_schools?.length > 0 ? (
          <>
            <div className="flex items-end justify-between mb-8">
              <div>
                <p className="font-label text-[10px] text-rosemetal tracking-widest">HEATING UP STATEWIDE</p>
                <h2 className="font-display text-4xl sm:text-5xl mt-2 leading-[0.95]">
                  Schools locking looks<br /><span className="italic text-rosemetal">this month.</span>
                </h2>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {trending.hot_schools.map((s, i) => (
                <Link key={s.id} to={`/schools/${s.slug || s.id}`} onClick={() => track("trending_school_click", { school_id: s.id, school_slug: s.slug, rank: i + 1 })} data-testid={`hot-school-${s.slug}`} className="group border border-border rounded-sm p-5 hover:border-rosemetal transition-colors">
                  <div className="font-label text-[9px] text-rosemetal tracking-widest">◉ HOT #{i + 1}</div>
                  <div className="font-display text-lg mt-3 group-hover:text-rosemetal leading-tight">{s.name}</div>
                  <div className="font-label text-[9px] text-muted-foreground mt-1 tracking-widest">{s.city}{s.mascot ? ` · ${s.mascot}` : ""}</div>
                  <div className="mt-4 font-display text-2xl">{s.locks_30d}</div>
                  <div className="font-label text-[9px] text-muted-foreground tracking-widest">LOCKS · 30 DAYS</div>
                </Link>
              ))}
            </div>
          </>
        ) : null}
      </section>

      {/* FEATURED */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 mt-28">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="font-label text-[10px] text-rosemetal mb-2">Most-locked this week</p>
            <h2 className="font-display text-4xl sm:text-5xl">The Editorial Picks</h2>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-10">
          {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </section>

      {/* TUXEDOS — For The Guys */}
      <section data-testid="tuxedos-showcase" className="mt-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-ink to-[#0e0d10]" />
        <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(1200px 500px at 80% 20%, rgba(230,196,143,0.25), transparent)" }} />
        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 py-24 md:py-28">
          <Reveal>
            <div className="flex items-end justify-between mb-10 text-ivory">
              <div>
                <p className="font-label text-[10px] text-champagne tracking-widest">FOR THE GUYS</p>
                <h2 className="font-display text-5xl sm:text-6xl mt-2 leading-[0.95]">
                  Tuxedos, tailored<br />
                  <span className="italic text-champagne">to the whole squad.</span>
                </h2>
                <p className="font-body text-sm text-ivory/70 mt-5 max-w-md">
                  Coordinate the crew — jackets and pants sized together, shoes and accessories to match, no lock required. From black-tie prom to homecoming date-night.
                </p>
              </div>
              <Link to="/tuxedos" data-testid="tuxedos-cta" className="hidden md:inline-flex items-center gap-2 border border-champagne text-champagne px-6 py-3 font-label text-[10px] rounded-full hover:bg-champagne hover:text-ink transition-colors">
                SHOP TUXEDOS <ArrowUpRight className="w-3 h-3" />
              </Link>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-10">
            {tuxedos.length === 0 && [0,1,2,3].map((i) => (
              <div key={i} className="aspect-[3/4] bg-ivory/5 rounded-sm animate-pulse" />
            ))}
            {tuxedos.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
          <div className="mt-10 md:hidden">
            <Link to="/tuxedos" className="inline-flex items-center gap-2 border border-champagne text-champagne px-6 py-3 font-label text-[10px] rounded-full">
              SHOP TUXEDOS <ArrowUpRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </section>

      {/* PROM CUP LEADERBOARD */}
      <section data-testid="prom-cup" className="max-w-[1400px] mx-auto px-5 sm:px-8 mt-32">
        <Reveal>
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="font-label text-[10px] text-rosemetal tracking-widest flex items-center gap-2">
                <Trophy className="w-3 h-3" /> THE PROM CUP · 2026 SEASON
              </p>
              <h2 className="font-display text-4xl sm:text-5xl mt-2 leading-[0.95]">
                Who's winning<br /> <span className="italic text-rosemetal">the season?</span>
              </h2>
            </div>
            <Link to="/prom-cup" className="hidden md:flex font-label text-[10px] text-rosemetal items-center gap-1">
              Full standings <ArrowUpRight className="w-3 h-3" />
            </Link>
          </div>
        </Reveal>
        <div className="border border-border rounded-sm overflow-hidden bg-ivory">
          <div className="grid grid-cols-[40px_1fr_80px_80px_100px] md:grid-cols-[60px_1fr_120px_120px_140px] px-5 py-3 border-b border-border font-label text-[9px] text-muted-foreground tracking-widest">
            <span>RANK</span>
            <span>SCHOOL</span>
            <span className="text-right">LOCKS</span>
            <span className="text-right hidden md:block">TRY-ONS</span>
            <span className="text-right">RAISED</span>
          </div>
          {leaderboard.length === 0 && (
            <div className="p-8 text-center font-body text-sm text-muted-foreground">
              Standings appear once schools start locking looks. Be the first — <Link to="/schools" className="text-rosemetal underline">find your school</Link>.
            </div>
          )}
          {leaderboard.map((row, i) => (
            <motion.div
              key={row.school_id}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="grid grid-cols-[40px_1fr_80px_80px_100px] md:grid-cols-[60px_1fr_120px_120px_140px] px-5 py-4 border-b border-border last:border-b-0 items-center hover:bg-blush/30 transition-colors group"
              data-testid={`leaderboard-row-${row.rank}`}
            >
              <div className="flex items-center gap-1.5">
                {row.rank === 1 && <Crown className="w-4 h-4 text-champagne" />}
                <span className={`font-display text-2xl ${row.rank <= 3 ? "text-rosemetal" : "text-muted-foreground"}`}>{row.rank}</span>
              </div>
              <Link to={`/schools/${row.school_slug}`} className="font-body">
                <div className="font-display text-lg leading-tight group-hover:text-rosemetal transition-colors">{row.school_name || "—"}</div>
                <div className="font-label text-[9px] text-muted-foreground mt-0.5 tracking-widest">
                  {row.city}{row.mascot ? ` · ${row.mascot}` : ""}
                </div>
              </Link>
              <div className="text-right font-display text-xl">{row.locks}</div>
              <div className="text-right font-display text-xl hidden md:block">{row.tryons}</div>
              <div className="text-right font-display text-xl text-rosemetal">${row.raised >= 1000 ? `${(row.raised/1000).toFixed(1)}K` : Math.round(row.raised)}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* STYLE MATCH + SQUAD PLANNER — two cinematic promos */}
      <section data-testid="home-promos" className="max-w-[1400px] mx-auto px-5 sm:px-8 mt-32 grid md:grid-cols-2 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
          className="relative overflow-hidden rounded-sm p-10 md:p-14 min-h-[380px] flex flex-col justify-end group"
          style={{ background: "linear-gradient(135deg, #B76E79 0%, #d78a84 50%, #E6C48F 100%)" }}
          data-testid="promo-style-match"
        >
          <div className="absolute inset-0 bg-ink/10 group-hover:bg-ink/0 transition-colors" />
          <span className="absolute top-8 right-8 w-16 h-16 rounded-full bg-cream/25 flex items-center justify-center backdrop-blur">
            <Sparkles className="w-6 h-6 text-cream" strokeWidth={1.5} />
          </span>
          <div className="relative text-cream">
            <p className="font-label text-[10px] text-cream/80 tracking-widest">60-SECOND STYLE MATCH</p>
            <h3 className="font-display text-4xl sm:text-5xl mt-2 leading-[0.95]">
              Your dream dress<br />in three answers.
            </h3>
            <p className="font-body text-sm text-cream/85 mt-4 max-w-sm">
              A quick quiz — event, vibe, palette, budget. AI ranks your top 3 dresses from the catalog. One tap to try them on.
            </p>
            <Link to="/style-match" data-testid="promo-style-match-cta" className="inline-flex items-center gap-2 mt-6 bg-cream text-ink px-6 py-3 rounded-full font-label text-[10px] tracking-widest hover:bg-ivory transition-colors">
              TAKE THE QUIZ <ArrowUpRight className="w-3 h-3" />
            </Link>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.1 }}
          className="relative overflow-hidden rounded-sm p-10 md:p-14 min-h-[380px] flex flex-col justify-end group bg-ink text-cream"
          data-testid="promo-squads"
        >
          <div className="absolute inset-0 opacity-70" style={{ background: "radial-gradient(400px 200px at 80% 20%, rgba(230,196,143,0.35), transparent)" }} />
          <div className="absolute -bottom-32 -left-20 w-96 h-96 rounded-full bg-rosemetal/25 blur-3xl" />
          <span className="absolute top-8 right-8 w-16 h-16 rounded-full bg-cream/10 border border-cream/20 flex items-center justify-center backdrop-blur">
            <Crown className="w-6 h-6 text-champagne" strokeWidth={1.5} />
          </span>
          <div className="relative">
            <p className="font-label text-[10px] text-champagne tracking-widest">SQUAD PLANNER · GROUP MODE</p>
            <h3 className="font-display text-4xl sm:text-5xl mt-2 leading-[0.95]">
              Coordinate your<br /><span className="italic text-champagne">whole prom court.</span>
            </h3>
            <p className="font-body text-sm text-cream/70 mt-4 max-w-sm">
              4+ girls = one stylist coordinator + matched palettes across dresses, tuxes, shoes, and accessories. No one shows up in the same look.
            </p>
            <Link to="/squads" data-testid="promo-squads-cta" className="inline-flex items-center gap-2 mt-6 bg-champagne text-ink px-6 py-3 rounded-full font-label text-[10px] tracking-widest hover:bg-cream transition-colors">
              BUILD YOUR SQUAD <ArrowUpRight className="w-3 h-3" />
            </Link>
          </div>
        </motion.div>
      </section>

      {/* COMMUNITY LOOKBOOK — student try-ons who opted to share */}
      {lookbook.length > 0 && (
      <section data-testid="community-lookbook" className="max-w-[1400px] mx-auto px-5 sm:px-8 mt-32">
        <Reveal>
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="font-label text-[10px] text-rosemetal tracking-widest">STUDENT LOOKBOOK · SHARED WITH LOVE</p>
              <h2 className="font-display text-4xl sm:text-5xl mt-2 leading-[0.95]">
                Real Wisconsin students,<br /><span className="italic text-rosemetal">real try-ons.</span>
              </h2>
            </div>
            <Link to="/lookbook" className="hidden md:flex font-label text-[10px] text-rosemetal items-center gap-1">
              See all <ArrowUpRight className="w-3 h-3" />
            </Link>
          </div>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {lookbook.map((l, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06 }}
              className="relative aspect-[3/4] overflow-hidden bg-blush rounded-sm group"
              data-testid={`lookbook-${i}`}
            >
              <img src={l.image_url} alt={`${l.first_name || 'student'} — ${l.event}`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/10 to-transparent" />
              <div className="absolute bottom-2 left-2 right-2 text-ivory">
                <div className="font-label text-[8px] text-champagne tracking-widest">{l.event}</div>
                <div className="font-display text-sm leading-tight">
                  {l.first_name}{l.school_name ? ` · ${l.school_name}` : ""}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>
      )}

      {/* SPLIT — Meet Heather */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 mt-32">
        <div className="grid md:grid-cols-2 gap-0 items-stretch">
          <div className="bg-ink text-ivory p-10 md:p-16 flex flex-col justify-center">
            <p className="font-label text-[10px] text-champagne mb-5">Meet the woman behind the atelier</p>
            <h2 className="font-display text-4xl sm:text-5xl leading-tight">Heather styles you<br />like her own.</h2>
            <p className="font-body text-sm text-ivory/70 mt-6 leading-relaxed max-w-md">
              {HEATHER_NAME}, {HEATHER_TITLE.toLowerCase()}, hand-picks every gown, guides your fit from your measurements, and can tailor your dress in-boutique so it's truly one-of-one. Describe your event and she'll build the whole look with you.
            </p>
            <ul className="mt-8 space-y-3 font-body text-sm text-ivory/80">
              <li>— AI fit guidance from your measurements</li>
              <li>— Virtual try-on with your own photo</li>
              <li>— Private in-boutique fittings with the Master Seamstress</li>
            </ul>
          </div>
          <div className="aspect-square md:aspect-auto bg-blush"><img src={HEATHER_HERO_IMG} alt={`${HEATHER_NAME}, ${HEATHER_TITLE}`} className="w-full h-full object-cover object-top" /></div>
        </div>
      </section>

      {/* MEMORIES teaser */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 mt-32">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="font-label text-[10px] text-rosemetal mb-2">The night, remembered</p>
            <h2 className="font-display text-4xl sm:text-5xl">Prom Memories</h2>
          </div>
          <Link to="/memories" className="font-label text-[10px] text-rosemetal flex items-center gap-1">View gallery <ArrowUpRight className="w-3 h-3" /></Link>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {memories.map((m, i) => (
            <div key={m.id} className="relative aspect-[3/4] overflow-hidden bg-blush animate-fade-up" style={{ animationDelay: `${i * 80}ms` }}>
              <img src={m.image_url} alt={m.caption} className="w-full h-full object-cover" />
              <div className="absolute bottom-3 left-3 right-3 glass text-ink px-3 py-1.5 font-body text-xs rounded-sm">{m.caption}</div>
            </div>
          ))}
        </div>
      </section>

      {/* VISIT THE BOUTIQUE — in-page map + directions */}
      <VisitBoutique />

      {/* FAQ — AEO */}
      <section className="max-w-[900px] mx-auto px-5 sm:px-8 mt-32" data-testid="home-faq">
        <p className="font-label text-[10px] text-rosemetal mb-2">Good to know</p>
        <h2 className="font-display text-4xl sm:text-5xl mb-10">Questions, answered</h2>
        <div className="divide-y divide-border border-y border-border">
          {FAQS.map((f, i) => (
            <details key={i} className="group py-6" data-testid={`faq-${i}`}>
              <summary className="font-display text-2xl cursor-pointer list-none flex justify-between items-center">
                {f.q}<span className="text-rosemetal text-3xl font-light group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="font-body text-sm text-muted-foreground mt-3 leading-relaxed max-w-2xl">{f.a}</p>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
}
